import { Link } from "@tanstack/react-router";
import { LogoMark } from "@/components/landing/LogoMark";
import { blogHref, docsHref } from "@/lib/routes";
import { SITE } from "@/content/marketing";
import { ContactUsLink } from "@/components/landing/ContactUsLink";

/**
 * The footer every public surface closes on.
 *
 * Extracted from `_home.tsx` when the blog moved onto the warm palette: the
 * blog is a marketing surface but is NOT under the `_home` layout, so before
 * this it was going to be a second copy of the same eight links. `Nav` stays in
 * `_home` on purpose — it takes `onStartFree`, which opens a dialog only that
 * layout mounts, and the blog's own header carries a plain `/login` CTA instead.
 *
 * On the ink, like the closing CTA above it on the landing page — the two read
 * as one slab there, and every sub-page gets the same dark ground so the chrome
 * stays one system. `.gs-inverse` remaps the colour tokens the links below read,
 * and it is defined under `.gs-site`, so this renders correctly only inside that
 * scope. Both call sites are `.gs-site` roots.
 */
export function SiteFooter({ blogOnly = false }: { blogOnly?: boolean }) {
  const docsUrl = docsHref();
  const blogUrl = blogHref();
  const navigation = blogOnly
    ? SITE.nav.filter((entry) => entry.to !== "/pricing")
    : SITE.nav;
  const item =
    "no-underline text-[color:var(--gs-muted)] hover:text-[color:var(--gs-text)] transition-colors";
  return (
    <footer className="gs-inverse text-[length:var(--gs-text-meta)] text-[color:var(--gs-muted)]">
      <div className="mx-auto w-full max-w-[1080px] border-t border-[color:var(--gs-border)] px-6 py-8">
        <div className="mb-4 flex items-center gap-2.5 text-[color:var(--gs-text)] font-semibold text-[length:var(--gs-text-card)] [font-family:var(--gs-font-display)]">
          <LogoMark size={24} />
          {SITE.wordmark}
        </div>
        <nav
          aria-label="Footer"
          className="flex flex-wrap items-center gap-x-6 gap-y-2"
        >
          {/* Unconditional at every width — the header's phone menu now lists
              the same pages, but the footer stays the always-visible path. */}
          {navigation.map((entry) => (
            <Link key={entry.to} to={entry.to} className={item}>
              {entry.label}
            </Link>
          ))}
          <Link to={SITE.compare.to} className={item}>
            {SITE.compare.label}
          </Link>
          {!blogOnly ? (
            <a
              href={docsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={item}
            >
              Docs
            </a>
          ) : null}
          <a
            href={blogUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={item}
          >
            Blog
          </a>
          {/* GoodSender's own policy and terms: the Joy Labs pages they
              replaced are Laneful's and never mention GoodSender, MCP or AI
              assistants, which the Claude and ChatGPT directory listings need
              them to. */}
          <Link to="/privacy" className={item}>
            Privacy
          </Link>
          <Link to="/terms" className={item}>
            Terms
          </Link>
          <ContactUsLink />
        </nav>
        <p className="mt-4">
          {SITE.footer.credibility} {SITE.footer.copyright}
        </p>
      </div>
    </footer>
  );
}
